import type {
  ApiErrorKind,
  ApiErrorResponse,
  ApiResponse,
  ValidationErrors,
} from "@shared/contracts";
import {
  flattenValidationErrors,
  isLaravelEnvelope,
  isValidationErrors,
  normalizeApiErrorMessage,
} from "@shared/contracts";

export { CLIENT_ERROR_KINDS } from "@shared/contracts";

/**
 * A real `Error` subclass carrying the typed `ApiErrorResponse`.
 *
 * Being an `Error` is the point: a plain object literal would make
 * `error instanceof Error` false and every 4xx/5xx would be swallowed by the
 * React Query pipeline. Consumers narrow with `instanceof ApiError` and read
 * fields directly.
 */
export class ApiError extends Error {
  readonly response: ApiErrorResponse;
  readonly statusCode: number;
  readonly kind: ApiErrorKind;
  readonly path: string;
  /** A 422's field map; `{}` for every other failure. */
  readonly fieldErrors: ValidationErrors;
  /** Flattened field messages on a 422, otherwise `[message]`. */
  readonly messages: readonly string[];

  constructor(response: ApiErrorResponse, options?: { cause?: unknown }) {
    super(normalizeApiErrorMessage(response), options);
    this.name = "ApiError";
    this.response = response;
    this.statusCode = response.statusCode;
    this.kind = response.kind;
    this.path = response.path;
    this.fieldErrors = response.errors ?? {};
    this.messages = response.errors
      ? flattenValidationErrors(response.errors)
      : [normalizeApiErrorMessage(response)];
  }

  /** The first validation message for one field — for binding to a form input. */
  fieldError(name: string): string | undefined {
    return this.fieldErrors[name]?.[0];
  }

  /**
   * Classify a non-2xx response.
   *
   * The single place the status → kind table lives. Two rows are lossy and
   * cannot be fixed client-side:
   * - **400** is both "unknown route" and "Spatie denied the role" — the
   *   backend's handler only maps the literal message `'Unauthorized Action'`
   *   to 403, so a role denial lands here.
   * - **404** is both "no such record" and, in production, *every* unhandled
   *   exception, because `Handler` ends with `rest()->noData()->send()`.
   *
   * Do not disambiguate by reading `message`: it is `trans()`-localized and any
   * such check breaks when a second locale is enabled.
   */
  static fromResponse(
    statusCode: number,
    parsed: unknown,
    path: string,
    rawText: string
  ): ApiError {
    if (!isLaravelEnvelope(parsed)) {
      return ApiError.malformed(path, statusCode, rawText.slice(0, 200));
    }

    const kind = ApiError.kindForStatus(statusCode);
    const errors =
      kind === "VALIDATION_ERROR" && isValidationErrors(parsed.data)
        ? parsed.data
        : undefined;

    return new ApiError({
      statusCode,
      message: parsed.message,
      kind,
      path,
      errors,
      raw: parsed,
    });
  }

  private static kindForStatus(statusCode: number): ApiErrorKind {
    switch (statusCode) {
      // 406 is `ApiResponse::tokenExpiration()`. Largely unreachable — the
      // `authenticated:api` middleware returns 401 first — but it means the
      // same thing: the session needs renewing.
      case 401:
      case 406:
        return "UNAUTHORIZED";
      case 403:
        return "FORBIDDEN";
      case 404:
        return "NOT_FOUND";
      case 422:
        return "VALIDATION_ERROR";
      case 400:
        return "BAD_REQUEST";
      default:
        return statusCode >= 500 ? "SERVER_ERROR" : "BAD_REQUEST";
    }
  }

  /** `fetch()` itself rejected — offline, DNS, CORS preflight, abort. */
  static network(path: string, cause: unknown): ApiError {
    return new ApiError(
      {
        statusCode: 0,
        kind: "NETWORK_ERROR",
        message: "",
        path,
      },
      { cause }
    );
  }

  /** The body was not the envelope the contract promises. */
  static malformed(path: string, statusCode: number, detail: string): ApiError {
    return new ApiError({
      statusCode,
      kind: "MALFORMED_RESPONSE",
      message: "",
      path,
      raw: detail,
    });
  }

  /**
   * HTTP said 2xx but the payload says the operation failed.
   *
   * Exists for exactly one endpoint: `BaseAuthController::login()` returns
   * `{ data: null, message: "Credentials not match records", code: 200 }` on
   * bad credentials, with no status setter. Delete this once login returns 401.
   */
  static businessFailure(
    response: ApiResponse<unknown>,
    path: string
  ): ApiError {
    return new ApiError({
      statusCode: response.statusCode,
      kind: "BUSINESS_ERROR",
      message: response.message,
      path,
      raw: response,
    });
  }

  static isApiError(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}
